import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from "../services/axiosInstance";

interface User {
  id: string;
  email: string;
  nom: string;
  roles: string[];
  isAdmin: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (token: string, refreshToken?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
     return null;
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('jwt_token');
      if (token) {
        const payload = parseJwt(token);
        if (payload && Array.isArray(payload.roles)) {
          const userData: User = {
            id: payload.user_id?.toString() || '',
            email: payload.email || '',
            nom: payload.nom || '',
            roles: payload.roles || [],
            isAdmin: payload.roles.includes('ROLE_ADMIN')
          };
          setUser(userData);
          setIsAuthenticated(true);
          try {
            const { data } = await axios.get<{ id: number; email: string; nom: string; roles: string[] }>("/api/me");
            const refreshed: User = {
              id: data.id?.toString() || userData.id,
              email: data.email || userData.email,
              nom: data.nom || userData.nom,
              roles: data.roles || userData.roles,
              isAdmin: (data.roles || userData.roles).includes('ROLE_ADMIN')
            };
            setUser(refreshed);
          } catch (_) {
            // ignore
          }
        } else {
          localStorage.clear();
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = async (token: string, refreshToken?: string) => {
    const payload = parseJwt(token);
    if (payload) {
      const userData: User = {
        id: payload.user_id?.toString() || '',
        email: payload.email || '',
        nom: payload.nom || '',
        roles: payload.roles || [],
        isAdmin: payload.roles.includes('ROLE_ADMIN')
      };

      localStorage.setItem('jwt_token', token);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      localStorage.setItem('user_roles', JSON.stringify(payload.roles || []));
      localStorage.setItem('user_email', payload.email || '');
      localStorage.setItem('user_id', payload.user_id?.toString() || '');
      localStorage.setItem('user_nom', payload.nom || '');

      setUser(userData);
      setIsAuthenticated(true);

      // Immediately refresh user from API to ensure latest profile info (name, roles)
      try {
        const { data } = await axios.get<{ id: number; email: string; nom: string; roles: string[] }>("/api/me");
        const refreshed: User = {
          id: data.id?.toString() || userData.id,
          email: data.email || userData.email,
          nom: data.nom || userData.nom,
          roles: data.roles || userData.roles,
          isAdmin: (data.roles || userData.roles).includes('ROLE_ADMIN')
        };
        setUser(refreshed);
      } catch (_) {
        // ignore
      }
    }
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');

     setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_roles');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_nom');

     if (refreshToken) {
      try {
        await axios.post('/api/logout', { refresh_token: refreshToken });
      } catch (_) {
       }
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
